'use client';

import { useCallback } from 'react';
import { usePathname } from 'next/navigation';
import MenuItem from '@mui/material/MenuItem';
import { useTheme } from '@mui/material/styles';
import LocalizedLink from '@/components/LocalizedLink';
import { ROUTES } from '@/constants/application-urls';
import useLocale from '@/hooks/useLocale';

export default function HeaderNavigationItem({
	url,
	title,
	onClick,
}: Readonly<{ url: string; title: string; onClick?: () => void }>) {
	const theme = useTheme();
	const pathname = usePathname();
	const locale = useLocale();

	const isActive = useCallback(
		(nav: string) => {
			const isHomePage = nav === ROUTES.HOME;

			if (isHomePage) return pathname === `/${locale}`;

			return pathname.startsWith(`/${locale}${nav}`);
		},
		[pathname, locale],
	);

	return (
		<MenuItem
			sx={{
				position: 'relative',
				minHeight: '33px',
				'&::after': {
					content: {
						xs: 'none',
						md: '""',
					},
					position: 'absolute',
					bottom: 0,
					left: 0,
					top: 0,
					marginTop: '38px',
					width: '100%',
					height: '3px',
					backgroundColor: isActive(url) ? theme.palette.primary.main : 'transparent',
					borderTopLeftRadius: '3px',
					borderTopRightRadius: '3px',
				},
				'&:last-of-type': {
					borderBottom: '1px solid',
					borderColor: {
						xs: 'grey.light',
						md: 'transparent',
					},
				},
				borderTop: '1px solid',
				borderColor: {
					xs: 'grey.light',
					md: 'transparent',
				},
				marginRight: {
					xs: '0',
					md: '32px',
				},
				padding: 0,
			}}
		>
			<LocalizedLink
				href={url}
				onClick={onClick}
				sx={{
					textDecoration: 'none',
					height: {
						xs: 'auto',
						md: '33px',
					},
					width: '100%',
					color: isActive(url) ? theme.palette.primary.main : theme.palette.black.main,
					fontWeight: {
						xs: '700',
						md: '500',
					},
					fontSize: {
						xs: '18px',
						md: '16px',
					},
					padding: {
						xs: '15px 20px',
						md: '0',
					},
				}}
			>
				{title}
			</LocalizedLink>
		</MenuItem>
	);
}
